import { Injectable } from '@angular/core'
import { CommandPaletteControllerService } from '../../../api/backend'
import { AuthContextStorageService } from '../../../core/auth/auth-context-storage.service'

// Persistence použití příkazů na BE pro cross-device sdílení. Žebříček „Nejčastější"
// (`{projectKey}/command-palette/most-visited`) POČÍTÁ A ŘADÍ BACKEND podle četnosti
// použití — FE jen zaznamenává každé použití přes POST `record` a čte hotový žebříček.
// (Recency endpoint `/recent` existuje taky, ale produktově jsme zvolili most-visited —
// viz DEV-546, lepší business value.)
//
// getMostVisitedIds() je SYNCHRONNÍ (komponenta ho volá synchronně v recomputeResults).
// Proto držíme warm in-memory cache plněnou lazy HTTP GET — load se spustí při prvním
// getMostVisitedIds() kdy je projectKey dostupný. První otevření palety vrátí
// prázdnou/cached hodnotu, při dalších otevřeních jsou data ze serveru k dispozici.
@Injectable({ providedIn: 'root' })
export class MostVisitedCommandsService {

    private static readonly MAX_ENTRIES = 20

    private cache: Array<string> = []
    private loaded = false
    private loading = false

    constructor(
        private readonly commandPaletteControllerService: CommandPaletteControllerService,
        private readonly authContext: AuthContextStorageService
    ) {}

    public recordExecution(commandId: string): void {
        const projectKey = this.authContext.getProjectKey()
        if (!projectKey) {
            return
        }
        // Žebříček řadí BE podle četnosti — lokálně nepřeřazujeme. Po zaznamenání
        // jen zneplatníme cache, aby se při dalším otevření načetl aktualizovaný
        // most-visited ranking ze serveru.
        this.commandPaletteControllerService.record(projectKey, { commandId }).subscribe({
            next: () => {
                this.loaded = false
            },
            error: () => {
                // fire-and-forget — selhání persistence nesmí rozbít UI
            }
        })
    }

    public getMostVisitedIds(limit: number = 5): Array<string> {
        if (!this.loaded && !this.loading && this.authContext.getProjectKey()) {
            this.loadFromServer()
        }
        return this.cache.slice(0, limit)
    }

    public clear(): void {
        // Mažeme in-memory cache a resetujeme loaded flag, aby mohl proběhnout
        // nový fetch při dalším getMostVisitedIds() (např. po přepnutí projektu).
        this.cache = []
        this.loaded = false
        this.loading = false
    }

    private loadFromServer(): void {
        const projectKey = this.authContext.getProjectKey()
        if (!projectKey) {
            return
        }
        this.loading = true
        this.commandPaletteControllerService.getMostVisited(projectKey, MostVisitedCommandsService.MAX_ENTRIES).subscribe({
            next: ids => {
                this.cache = Array.isArray(ids)
                    ? ids.filter(id => typeof id === 'string').slice(0, MostVisitedCommandsService.MAX_ENTRIES)
                    : []
                this.loaded = true
                this.loading = false
            },
            error: () => {
                // BE nedostupné — necháme prázdnou cache, paleta funguje bez Nejčastějších.
                // Nenastavujeme loaded=true, aby se při příštím getMostVisitedIds() zkusil
                // retry (může projít po obnovení spojení).
                this.loading = false
            }
        })
    }
}